/**
 * Pending owner submissions, rendered in the column beside the property grid.
 *
 * The queue used to live one page away, as the third tab of Owners & access: a
 * building waiting to become real was filed under the person who sent it, not
 * under the buildings. The nav pip said a submission existed; finding it meant
 * knowing which page it had been filed on. So it sits here instead, in the
 * column the page was already reserving and leaving empty.
 *
 * "Review" opens the full sheet — the units, the rooms, the rents, the note
 * box, Approve or Reject. A 320px rail is the right width to NOTICE a
 * submission and the wrong width to decide on one.
 *
 * Only PENDING rows appear: this is the queue, not a history — once a
 * submission is decided, it drops off here and (if approved) shows up as an
 * ordinary property in the grid beside it.
 */
import { useMemo, useState } from "react";
import { Building2, CheckCircle2, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { formatDate } from "@/lib/v2-format";
import { PropertySubmissionReviewSheet } from "./PropertySubmissionReviewSheet";
import {
  useAdminPropertySubmissions,
  type PropertySubmissionRow,
} from "@/hooks/v2/usePropertySubmissions";
import { useLanguage } from "@/contexts/LanguageContext";

/**
 * Above this many pending, the list scrolls inside the rail instead of growing
 * the page.
 *
 * It scrolls rather than truncating: fifteen submissions is exactly when a
 * queue needs working through, and a rail that shows five of them with the
 * rest behind a link sends you to another page for the sixth. The height caps
 * so the rail can't outgrow the property list beside it; nothing is hidden,
 * and the header count always states the true total.
 */
const SCROLL_AFTER = 4;

export function PropertyApprovalsRail({
  canDecide = true,
  className,
}: {
  /** Super Admin / Admin. Anyone else reads the submission without deciding. */
  canDecide?: boolean;
  className?: string;
}) {
  const { t } = useLanguage();
  const { data, isLoading } = useAdminPropertySubmissions();
  const [reviewing, setReviewing] = useState<PropertySubmissionRow | null>(
    null
  );

  // Already newest-first from the query (`submitted_at desc`).
  const pending = useMemo(
    () => (data ?? []).filter((r) => r.approval_status === "pending"),
    [data]
  );
  const scrolls = pending.length > SCROLL_AFTER;

  return (
    <>
      <Card
        className={cn(
          "overflow-hidden rounded-2xl border-border/60 bg-background/70 shadow-sm backdrop-blur supports-[backdrop-filter]:bg-background/60",
          className
        )}
      >
        <div className="border-b border-border/60 px-4 py-3">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-semibold text-foreground">
              {t("admin.approvals.rail.title", "Approvals")}
            </h2>
            {pending.length > 0 && (
              <span className="rounded-full border border-amber-200 bg-amber-100 px-2 py-0.5 text-[11px] font-semibold tabular-nums text-amber-700">
                {`${pending.length} ${t("admin.approvals.rail.waitingSuffix", "waiting")}`}
              </span>
            )}
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            {t(
              "admin.approvals.rail.subtitle",
              "Buildings owners submitted that nobody has decided on. None of them are live yet."
            )}
          </p>
        </div>

        {isLoading ? (
          <div className="space-y-3 p-4">
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className="space-y-2 rounded-xl border p-3">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
                <Skeleton className="h-7 w-full" />
              </div>
            ))}
          </div>
        ) : pending.length === 0 ? (
          // The rail stays put when the queue is empty. One that disappears is
          // one people stop trusting to be where submissions arrive.
          <div className="px-4 py-8 text-center">
            <CheckCircle2 className="mx-auto h-5 w-5 text-emerald-600" />
            <p className="mt-2 text-xs text-muted-foreground">
              {t(
                "admin.approvals.rail.empty",
                "Nothing waiting. Owner submissions land here."
              )}
            </p>
          </div>
        ) : (
          <div
            className={cn(
              "space-y-3 p-4",
              // A scrollbar only appears once there is something below the
              // fold. A short queue keeps the rail exactly as tall as it needs
              // to be.
              scrolls && "max-h-[62vh] overflow-y-auto"
            )}
          >
            {pending.map((row) => (
              <PendingSubmissionCard
                key={row.id}
                row={row}
                canDecide={canDecide}
                onReview={() => setReviewing(row)}
              />
            ))}
          </div>
        )}
      </Card>

      <PropertySubmissionReviewSheet
        submission={reviewing}
        onClose={() => setReviewing(null)}
        canDecide={canDecide}
      />
    </>
  );
}

function PendingSubmissionCard({
  row,
  canDecide,
  onReview,
}: {
  row: PropertySubmissionRow;
  canDecide: boolean;
  onReview: () => void;
}) {
  const { t } = useLanguage();
  const waiting = waitingLabel(t, row.submitted_at);

  return (
    <div className="rounded-xl border border-l-[3px] border-border/70 border-l-amber-400 bg-card p-3">
      <div className="flex items-start gap-2">
        <Building2 className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold leading-tight">
            {row.title}
          </p>
          <p className="truncate text-xs text-muted-foreground">
            {row.address}
            {row.address && (row.zip_code || row.city) ? ", " : ""}
            {row.zip_code ? `${row.zip_code} ` : ""}
            {row.city}
          </p>
        </div>
      </div>

      <p className="mt-2 truncate text-xs text-muted-foreground">
        {row.owner_name ??
          row.owner_email ??
          t("admin.approvals.rail.unknownOwner", "Unknown owner")}
        {row.submitted_at ? ` · ${formatDate(row.submitted_at)}` : ""}
      </p>

      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <Fact>
          {row.apartments_count}{" "}
          {row.apartments_count === 1
            ? t("admin.approvals.rail.apt", "apt")
            : t("admin.approvals.rail.apts", "apts")}
        </Fact>
        <Fact>
          {row.rooms_count}{" "}
          {row.rooms_count === 1
            ? t("admin.approvals.rail.room", "room")
            : t("admin.approvals.rail.rooms", "rooms")}
        </Fact>
        {waiting && (
          <span className="inline-flex items-center gap-1 rounded-md bg-amber-50 px-1.5 py-0.5 text-[11px] font-medium text-amber-700">
            <Clock className="h-3 w-3" />
            {waiting}
          </span>
        )}
      </div>

      <Button
        type="button"
        size="sm"
        variant={canDecide ? "default" : "outline"}
        className="mt-3 h-8 w-full rounded-lg"
        onClick={onReview}
      >
        {canDecide
          ? t("admin.approvals.rail.review", "Review")
          : t("admin.approvals.rail.view", "View")}
      </Button>
    </div>
  );
}

function Fact({ children }: { children: React.ReactNode }) {
  return (
    <span className="rounded-md bg-muted px-1.5 py-0.5 text-[11px] tabular-nums text-muted-foreground">
      {children}
    </span>
  );
}

/**
 * How long this has been sitting there — the fact a queue is actually read for.
 * Falls back to the submission date when the timestamp is unusable, and to
 * nothing at all when there is no date either, rather than printing "NaN days".
 */
function waitingLabel(
  t: ReturnType<typeof useLanguage>["t"],
  submittedAt: string | null
): string | null {
  if (!submittedAt) return null;
  const submitted = new Date(submittedAt);
  if (Number.isNaN(submitted.getTime())) return null;
  const days = Math.floor((Date.now() - submitted.getTime()) / 86_400_000);
  if (days < 0) return formatDate(submittedAt);
  if (days === 0) return t("admin.approvals.rail.today", "today");
  if (days === 1) return t("admin.approvals.rail.oneDay", "1 day");
  return `${days} ${t("admin.approvals.rail.daysSuffix", "days")}`;
}
